import { SelectChangeEvent } from '@material-ui/core';
import HelpIcon from '@mui/icons-material/Help';
import { Button, FormControl, Grid, IconButton, InputLabel, MenuItem, Tooltip } from '@mui/material';
import Select from '@mui/material/Select';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Label from 'src/components/Label';
import { StyledLoading } from 'src/components/app-components/StyledLoading';
import { StyledModal } from 'src/components/app-components/StyledModal';
import RHFTextField from 'src/components/react-hook-form/RHFTextField';
import {
  BULK_CREATE_EMAIL_BAD_REQUEST,
  BULK_CREATE_EMAIL_ETC_ERROR,
  BULK_CREATE_EMAIL_NO_ACCESS,
  BULK_CREATE_EMAIL_NO_ADDRESS,
  SELECT_RESPONDENT_LINE_MAX_LENGTH,
} from 'src/constants';
import ReturndReportContent from '../../../tab-containers/shared-tabs/respondent-tab/components/ReturndReportContent';
import useBulkCreateEmailModal from '../hooks/useBulkCreateEmailModal';
import { RegisterUnregisteredUserButton } from './RegisterUnregisteredUserButton';

export type FormValues = {
  emails: string;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  isPersonalEnquete: boolean;
}

const BulkCreateEmailModal = ({ isOpen, onClose, isPersonalEnquete }: Props) => {
  const { t } = useTranslation();
  const { control, formState, handleSubmit, reset, setValue, trigger, getValues } = useForm<FormValues>({
    mode: 'onChange',
  });
  const { onClick, toArrayEmailsString } = useBulkCreateEmailModal();
  const tKey = 'enqueteCreate.bulkCreateEmailModal';
  const rowLength = 8;
  const [errMessage, setErrMessage] = useState<string>('');
  const [isStyledLoading, setIsStyledLoading] = useState(false);
  const [isOpenReturndReportContent, setIsOpenReturndReportContent] = useState<boolean>(false);
  const [orgLevel, setOrgLevel] = useState<'' | 'division' | 'sbu' | 'department' | 'group'>('');
  const [returnedRegistUser, setReturnedRegistUser] = useState<string>('');

  const handleOnChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (value === 'division' || value === 'sbu' || value === 'department' || value === 'group') {
      setOrgLevel(value);
    } else {
      setOrgLevel('');
    }
  };

  const storageKey = 'BulkCreateEmailModal';
  const enterEmailsKey = 'EnterEmails';
  const errMessageKey = 'ErrMessage';
  const orgLevelKey = 'OrgLevel';

  const { id: rrId } = useParams();

  const saveLocalStorage = (enterEmails: string[], errMessage: string, orgLevelSave: string) => {
    localStorage.setItem(`${storageKey}${enterEmailsKey}${rrId}`, JSON.stringify(enterEmails.length > 0 ? enterEmails : []));
    localStorage.setItem(`${storageKey}${errMessageKey}${rrId}`, errMessage ? JSON.stringify(errMessage) : '');
    localStorage.setItem(`${storageKey}${orgLevelKey}${rrId}`, orgLevelSave);
  };

  const dispLocalstrageData = () => {
    const enterEmailsString: string | null = localStorage.getItem(`${storageKey}${enterEmailsKey}${rrId}`);
    const enterEmailsParse: string[] = enterEmailsString ? JSON.parse(enterEmailsString) : [];
    setValue('emails', enterEmailsParse.join(',').replace(/,/g, '\n'));

    const errMessageString = localStorage.getItem(`${storageKey}${errMessageKey}${rrId}`);
    setErrMessage(errMessageString ? JSON.parse(errMessageString) : '');

    const orgLevelString = localStorage.getItem(`${storageKey}${orgLevelKey}${rrId}`);
    if (orgLevelString === 'division' || orgLevelString === 'sbu' || orgLevelString === 'department' || orgLevelString === 'group') {
      setOrgLevel(orgLevelString);
    } else {
      setOrgLevel('');
    }
  };

  useEffect(() => {
    dispLocalstrageData();
  }, []); // eslint-disable-line

  return (
    <>
      <StyledModal isOpen={isOpen} modalTitle={t(`${tKey}.title`)} onCloseFunc={onClose} mode="BulkCreateEmailModal">
        <Grid container spacing={1} alignItems="flex-end" style={{ minHeight: '75px', maxHeight: '150px' }}>
          <Grid item xs={6} container justifyContent="space-between">
            <Label
              children={t(`${tKey}.explanation`)}
              color="info"
              sx={{ whiteSpace: 'pre-line', height: 'auto', lineHeight: 'normal', p: 1, mb: 2 }}
            />
            {!isPersonalEnquete && (
              <Grid item alignItems="center">
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>{t(`${tKey}.orgLevel`)}</InputLabel>
                  <Select value={orgLevel} onChange={handleOnChange} label={t(`${tKey}.orgLevel`)} sx={{ width: 250 }}>
                    <MenuItem value="division">{t(`common.divisionName`)}</MenuItem>
                    <MenuItem value="sbu">{t(`common.sbuName`)}</MenuItem>
                    <MenuItem value="department">{t(`common.departmentName`)}</MenuItem>
                    <MenuItem value="group">{t(`common.groupName`)}</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip
                  title={t(`${tKey}.tooltip.orgLevelText`)
                    .split('\n')
                    .map((text) => (
                      <div key={text}>{text}</div>
                    ))}>
                  <IconButton style={{ marginTop: '10px' }}>
                    <HelpIcon color="primary" sx={{ pt: '0px', pb: '0px', cursor: 'pointer' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Grid>
          <Grid item xs={6}>
            {t(`${tKey}.bulkCreateEmailError`)}
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Grid container sx={{ mt: 2 }}>
              <RHFTextField
                name="emails"
                label={''}
                control={control}
                rows={rowLength}
                multiline={true}
                fullWidth
                placeholder={t(`${tKey}.placeholder`)}
                rules={{
                  required: { value: true, message: t('validateError.required', { target: t('common.email') }) },
                  validate: {
                    isValidEmails: (email: string) => {
                      const emails = toArrayEmailsString(email);
                      const isValidEmail = (email: string) => {
                        return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(email);
                      };
                      for (const email of emails) {
                        if (!isValidEmail(email)) return t('validateError.format.email');
                      }
                      return true;
                    },
                    isOverEmailsCount: (email: string) => {
                      const emails = toArrayEmailsString(email);
                      if (SELECT_RESPONDENT_LINE_MAX_LENGTH < emails.length)
                        return t(`${tKey}.maxCount`, { max: SELECT_RESPONDENT_LINE_MAX_LENGTH.toLocaleString() });
                      return true;
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Grid container sx={{ mt: 2 }}>
              <RHFTextField
                name="errEmails"
                label={''}
                control={control}
                rows={rowLength}
                multiline={true}
                fullWidth
                placeholder={''}
                value={errMessage}
                inputProps={{ style: { color: 'red' }, readOnly: true }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container sx={{ mt: 3, justifyContent: 'space-between' }}>
          <Grid item>
            <Button
              variant={'contained'}
              onClick={() => {
                onClose();
              }}
              children={t('button.cancel')}
              color="error"
            />
          </Grid>
          <Grid item sx={{ display: 'row' }}>
            <RegisterUnregisteredUserButton
              errorMessage={errMessage}
              setErrorMessage={setErrMessage}
              setIsLoading={setIsStyledLoading}
              setValue={setValue}
              getValues={getValues}
            />
            {errMessage && (
              <Button
                variant={'contained'}
                onClick={handleSubmit(async (data: FormValues) => {
                  setIsStyledLoading(true);
                  const emailsList = toArrayEmailsString(data.emails);
                  const errEmails = toArrayEmailsString(errMessage);
                  const emails: string[] = emailsList.filter((email) => !errEmails.includes(email));
                  const result = await onClick(emails, errEmails, orgLevel);

                  let errMessageNoAddressError: string[] = [];
                  let errMessageRetrievalError: string[] = [];
                  const indexOfStrng = t(`${tKey}.error.mailAddressOrganizationRetrievalError`).split('\n');
                  const indexRetrievalError = errEmails.indexOf(indexOfStrng[0]);

                  switch (indexRetrievalError) {
                    case -1:
                      errMessageNoAddressError = errEmails;
                      break;
                    default:
                      // memo:
                      // 以下の処理は、登録エラー欄にメールアドレスを追加します
                      // 追加する際、以下の２つに分けられます
                      // 　・Marsポータル、bluePageに未登録のメールアドレス　（表示欄の上に表示します）
                      // 　・組織情報解決が出来ないメールアドレス　（表示欄の下に表示します）
                      // ローカルストレージには、両方をmergeした文字列として格納されており、メールアドレスを追加する場合は分離する必要があります
                      // 分離するキーは、組織解決が出来ない場合の文字列で行っており、日本語/英語とも2行です
                      // このため、組織解決が出来ない文字列を取得するためには、 indexRetrievalError + 2行 とする必要があり、sliceの引数を「 + 2」しています
                      errMessageNoAddressError = errEmails.slice(0, indexRetrievalError);
                      errMessageRetrievalError = errEmails.slice(indexRetrievalError + 2);
                      break;
                  }

                  switch (result?.status) {
                    case undefined:
                      trigger('emails');
                      saveLocalStorage([], errMessage, orgLevel);
                      reset();
                      if (result?.returnedRegistUser) {
                        errMessageRetrievalError.unshift(t(`${tKey}.error.mailAddressOrganizationRetrievalError`));
                        setReturnedRegistUser(result?.returnedRegistUser);
                        setIsOpenReturndReportContent(true);
                      }
                      if (result?.responseNoAddressError) {
                        errMessageNoAddressError.push(...toArrayEmailsString(result.responseNoAddressError));
                      }
                      if (result?.responseOrganizationError) {
                        errMessageRetrievalError.push(...toArrayEmailsString(result.responseOrganizationError));
                      }
                      if (errMessageRetrievalError.length > 0)
                        errMessageRetrievalError.unshift(t(`${tKey}.error.mailAddressOrganizationRetrievalError`));

                      if (result?.returnedRegistUser) {
                        setReturnedRegistUser(result.returnedRegistUser);
                        setIsOpenReturndReportContent(true);
                      } else {
                        setReturnedRegistUser('');
                        setIsOpenReturndReportContent(false);
                      }
                      break;

                    case BULK_CREATE_EMAIL_BAD_REQUEST:
                      saveLocalStorage(emailsList, errMessage, orgLevel);
                      break;

                    case BULK_CREATE_EMAIL_NO_ACCESS:
                      trigger('emails');
                      reset();
                      break;

                    case BULK_CREATE_EMAIL_NO_ADDRESS:
                      if (result.responseNoAddressError) {
                        errMessageNoAddressError.push(...toArrayEmailsString(result.responseNoAddressError));
                      }
                      if (result.responseOrganizationError) {
                        errMessageRetrievalError.push(...toArrayEmailsString(result.responseOrganizationError));
                      }
                      if (errMessageRetrievalError.length > 0)
                        errMessageRetrievalError.unshift(t(`${tKey}.error.mailAddressOrganizationRetrievalError`));

                      let localStrageErrMessage: string = '';
                      const appendErrors = (errors: string[]) => {
                        if (errors.length > 0) {
                          localStrageErrMessage += (localStrageErrMessage === '' ? '' : '\n') + errors.join('\n');
                        }
                      };
                      appendErrors(errMessageNoAddressError);
                      appendErrors(errMessageRetrievalError);

                      setErrMessage(localStrageErrMessage);
                      saveLocalStorage(emailsList, localStrageErrMessage, orgLevel);

                      if (result.returnedRegistUser) {
                        setReturnedRegistUser(result.returnedRegistUser);
                        setIsOpenReturndReportContent(true);
                      } else {
                        setReturnedRegistUser('');
                        setIsOpenReturndReportContent(false);
                      }
                      break;

                    case BULK_CREATE_EMAIL_ETC_ERROR:
                      saveLocalStorage(emailsList, errMessage, orgLevel);
                      break;

                    default:
                      saveLocalStorage(emailsList, errMessage, orgLevel);
                      break;
                  }
                  dispLocalstrageData();
                  setIsStyledLoading(false);
                })}
                children={t(`${tKey}.button.RegisterExcludingUnregistrableAddresses`)}
                sx={{ ml: 2 }}
                disabled={!formState.isValid || (!isPersonalEnquete && orgLevel === '')}
              />
            )}
            <Button
              variant={'contained'}
              onClick={handleSubmit(async (data: FormValues) => {
                setIsStyledLoading(true);
                const emailsList = toArrayEmailsString(data.emails);
                const result = await onClick(emailsList, [], orgLevel);
                let errMessageNewNoAddressError: string[] = [];
                let errMessageNewRetrievalError: string[] = [];

                switch (result?.status) {
                  case undefined:
                    saveLocalStorage([], '', '');
                    reset();
                    onClose();
                    if (result?.returnedRegistUser) {
                      setReturnedRegistUser(result?.returnedRegistUser);
                      setIsOpenReturndReportContent(true);
                    } else {
                      setReturnedRegistUser('');
                      setIsOpenReturndReportContent(false);
                    }
                    break;

                  case BULK_CREATE_EMAIL_BAD_REQUEST:
                    saveLocalStorage(emailsList, errMessage, orgLevel);
                    break;

                  case BULK_CREATE_EMAIL_NO_ACCESS:
                    trigger('emails');
                    reset();
                    break;

                  case BULK_CREATE_EMAIL_NO_ADDRESS:
                    if (result.responseNoAddressError) errMessageNewNoAddressError.push(...toArrayEmailsString(result.responseNoAddressError));
                    if (result.responseOrganizationError)
                      errMessageNewRetrievalError.push(
                        t(`${tKey}.error.mailAddressOrganizationRetrievalError`),
                        ...toArrayEmailsString(result.responseOrganizationError),
                      );

                    let resistErrMessage = errMessageNewNoAddressError.join('\n');
                    if (resistErrMessage && errMessageNewRetrievalError.length > 0)
                      resistErrMessage = resistErrMessage + '\n' + errMessageNewRetrievalError.join('\n');
                    if (!resistErrMessage && errMessageNewRetrievalError.length > 0) resistErrMessage = errMessageNewRetrievalError.join('\n');

                    setErrMessage(resistErrMessage);
                    saveLocalStorage(emailsList, resistErrMessage, orgLevel);

                    if (result.returnedRegistUser && !result.responseNoAddressError && !result.responseOrganizationError) {
                      setReturnedRegistUser(result.returnedRegistUser);
                      setIsOpenReturndReportContent(true);
                    } else {
                      setReturnedRegistUser('');
                      setIsOpenReturndReportContent(false);
                    }

                    if (!result.responseNoAddressError && !result.responseOrganizationError) {
                      saveLocalStorage([], '', '');
                      reset();
                      onClose();
                    }

                    break;

                  case BULK_CREATE_EMAIL_ETC_ERROR:
                    saveLocalStorage(emailsList, errMessage, orgLevel);
                    break;

                  default:
                    saveLocalStorage(emailsList, errMessage, orgLevel);
                    break;
                }
                dispLocalstrageData();
                setIsStyledLoading(false);
              })}
              children={t('button.register')}
              sx={{ ml: 2 }}
              disabled={!formState.isValid || (!isPersonalEnquete && orgLevel === '')}
            />
          </Grid>
        </Grid>
      </StyledModal>

      {/** 休職者登録レポートモーダル */}
      <StyledModal
        isOpen={isOpenReturndReportContent}
        mode="ReturndReportContent"
        onCloseFunc={() => setIsOpenReturndReportContent(false)}
        modalTitle={t('enqueteCreate.returndReportContent.title')}
        children={<ReturndReportContent errorReport={returnedRegistUser} onClose={() => setIsOpenReturndReportContent(false)} />}
      />

      <StyledLoading isOpen={isStyledLoading} />
    </>
  );
};

export default BulkCreateEmailModal;
